/**
 * Utility functions for measuring and sizing text to fit within bounds
 */

/**
 * Calculate optimal font size for text to fit within a given width
 * Uses binary search for efficiency
 */
export function calculateOptimalFontSize(
  text: string,
  maxWidth: number,
  fontFamily: string,
  minSize: number = 20,
  maxSize: number = 120,
  ctx?: CanvasRenderingContext2D
): number {
  // Create a temporary canvas context if not provided
  const tempCanvas = ctx || document.createElement('canvas').getContext('2d');
  if (!tempCanvas) return minSize;

  let low = minSize;
  let high = maxSize;
  let optimal = minSize;

  // Binary search for optimal font size
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    tempCanvas.font = `${mid}px "${fontFamily}"`;
    const metrics = tempCanvas.measureText(text);
    
    if (metrics.width <= maxWidth) {
      optimal = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return optimal;
}

/**
 * Calculate how many items fit within a given height
 */
export function calculateVisibleItems(
  items: string[],
  availableHeight: number,
  itemHeight: number,
  startY: number = 0
): { visible: string[], overflow: number } {
  const maxItems = Math.floor((availableHeight - startY) / itemHeight);
  const visibleItems = items.slice(0, maxItems);
  const overflow = Math.max(0, items.length - maxItems);

  return {
    visible: visibleItems,
    overflow
  };
}

/**
 * Word wrap text to fit within a given width
 */
export function wordWrapText(
  text: string,
  maxWidth: number,
  fontSize: number,
  fontFamily: string,
  ctx?: CanvasRenderingContext2D
): string[] {
  const tempCanvas = ctx || document.createElement('canvas').getContext('2d');
  if (!tempCanvas) return [text];

  tempCanvas.font = `${fontSize}px "${fontFamily}"`;
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const metrics = tempCanvas.measureText(testLine);

    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

/**
 * Generate a slug from artist name for URLs
 */
export function generateArtistSlug(artistName: string): string {
  return artistName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 30); // Limit length for readability
}